import React, { useCallback, useEffect, useRef, useState } from "react";

import { useLocation } from "react-router-dom";

import {

  fetchPendingAutomationSurveyPrompts,

  markOperatorNotificationsReadByDeepLink,

} from "../api/client";

import { useSocketContext } from "../context/SocketContext";

import {
  AUTOMATION_SURVEY_PROMPT_CHECK_EVENT,
  readAutomationSurveyPromptCheckDetail,
} from "../utils/automationSurveyPromptBus";
import { getActiveWorkspaceSlug } from "../utils/workspaceStorage";
import AutomationSurveyModal from "./AutomationSurveyModal";

const POLL_MS = 45_000;

function clearAutomationSurveySearchParam() {
  if (typeof window === "undefined") return;
  try {
    const url = new URL(window.location.href);
    if (!url.searchParams.has("automationSurvey")) return;
    url.searchParams.delete("automationSurvey");
    const next = `${url.pathname}${url.search}${url.hash}`;
    window.history.replaceState(window.history.state, "", next);
  } catch {
    /* ignore */
  }
}



function parseAutomationSurveyFromSearch(search) {

  try {

    const params = new URLSearchParams(search || "");

    const raw = params.get("automationSurvey");

    const id = Number(raw);

    return Number.isFinite(id) && id > 0 ? id : null;

  } catch {

    return null;

  }

}



function parseLeadIdFromPath(pathname) {

  const m = String(pathname || "").match(/\/chat\/(\d+)/);

  return m ? Number(m[1]) : null;

}



function parseSocketSurveyPromptPayload(raw) {

  if (raw == null) return null;

  let payload = raw;

  if (typeof raw === "string") {

    const trimmed = raw.trim();

    if (!trimmed) return null;

    try {

      payload = JSON.parse(trimmed);

    } catch {

      return null;

    }

  }

  if (!payload || typeof payload !== "object") return null;

  const promptId = Number(payload.promptId);

  const solicitacaoId = Number(payload.solicitacaoId);

  if (!Number.isFinite(promptId) || promptId <= 0) return null;

  return {

    promptId,

    solicitacaoId: Number.isFinite(solicitacaoId) && solicitacaoId > 0 ? solicitacaoId : null,

  };

}



export default function AutomationSurveyPromptHost() {

  const location = useLocation();
  const tenantSlug = getActiveWorkspaceSlug();

  const { socket } = useSocketContext();

  const [activePrompt, setActivePrompt] = useState(null);

  const [modalOpen, setModalOpen] = useState(false);

  const queueRef = useRef([]);

  const handledRef = useRef(new Set());

  const showingRef = useRef(false);



  const showNextFromQueue = useCallback(() => {

    if (showingRef.current) return;

    const next = queueRef.current.shift();

    if (!next) return;

    showingRef.current = true;

    setActivePrompt(next);

    setModalOpen(true);

  }, []);



  const enqueuePrompts = useCallback(

    (prompts = []) => {

      for (const p of prompts) {

        const id = Number(p?.id);

        if (!id || handledRef.current.has(id)) continue;

        if (queueRef.current.some((item) => Number(item.id) === id)) continue;

        queueRef.current.push(p);

      }

      showNextFromQueue();

    },

    [showNextFromQueue]

  );



  const pollPending = useCallback(async () => {

    if (!tenantSlug) return;
    if (showingRef.current) return;

    try {

      const leadId = parseLeadIdFromPath(location.pathname);

      const prompts = await fetchPendingAutomationSurveyPrompts(leadId);

      enqueuePrompts(prompts);

    } catch {

      /* ignore */

    }

  }, [location.pathname, enqueuePrompts, tenantSlug]);



  const openPromptById = useCallback(

    async (promptId) => {

      if (!tenantSlug) return;
      if (!promptId || handledRef.current.has(promptId) || showingRef.current) return;

      try {

        const leadId = parseLeadIdFromPath(location.pathname);

        const prompts = await fetchPendingAutomationSurveyPrompts(leadId);

        const match =

          prompts.find((p) => Number(p.id) === promptId) ||

          (await fetchPendingAutomationSurveyPrompts(null)).find((p) => Number(p.id) === promptId);

        if (match) {

          enqueuePrompts([match]);

          const deepLink = `/chat/${match.solicitacaoId}?automationSurvey=${match.id}`;

          markOperatorNotificationsReadByDeepLink(deepLink).catch(() => {});

        }

      } catch {

        /* ignore */

      }

    },

    [location.pathname, enqueuePrompts, tenantSlug]

  );



  useEffect(() => {

    if (!tenantSlug) return undefined;
    pollPending();

    const timer = setInterval(pollPending, POLL_MS);

    return () => clearInterval(timer);

  }, [pollPending, tenantSlug]);



  useEffect(() => {

    if (!tenantSlug) return;
    const promptId = parseAutomationSurveyFromSearch(location.search);

    if (promptId) openPromptById(promptId);

  }, [location.search, openPromptById, tenantSlug]);



  useEffect(() => {

    if (!tenantSlug) return undefined;
    const onCheck = (event) => {

      const leadId = readAutomationSurveyPromptCheckDetail(event);

      const currentLeadId = parseLeadIdFromPath(location.pathname);

      if (leadId != null && currentLeadId != null && leadId !== currentLeadId) return;

      window.setTimeout(() => pollPending(), 100);

    };

    window.addEventListener(AUTOMATION_SURVEY_PROMPT_CHECK_EVENT, onCheck);

    return () => window.removeEventListener(AUTOMATION_SURVEY_PROMPT_CHECK_EVENT, onCheck);

  }, [location.pathname, pollPending, tenantSlug]);



  useEffect(() => {

    if (!tenantSlug || !socket) return undefined;



    const handleSocketPrompt = (raw) => {

      const parsed = parseSocketSurveyPromptPayload(raw);

      if (!parsed) {

        pollPending();

        return;

      }



      const currentLeadId = parseLeadIdFromPath(location.pathname);

      if (parsed.solicitacaoId && currentLeadId && parsed.solicitacaoId !== currentLeadId) {

        return;

      }



      openPromptById(parsed.promptId);

    };



    socket.on("automationSurveyPrompt", handleSocketPrompt);

    return () => socket.off("automationSurveyPrompt", handleSocketPrompt);

  }, [socket, location.pathname, openPromptById, pollPending, tenantSlug]);



  const handleClose = useCallback(({ completed, dismissed } = {}) => {
    const id = Number(activePrompt?.id);
    if (id) handledRef.current.add(id);
    clearAutomationSurveySearchParam();
    setModalOpen(false);
    setActivePrompt(null);
    showingRef.current = false;
    if (completed || dismissed) {
      setTimeout(showNextFromQueue, 300);
    } else {
      showNextFromQueue();
    }
  }, [showNextFromQueue]);

  const handleCompleted = useCallback(() => {
    const id = Number(activePrompt?.id);
    if (id) handledRef.current.add(id);
    clearAutomationSurveySearchParam();
    if (id && activePrompt?.solicitacaoId) {
      const deepLink = `/chat/${activePrompt.solicitacaoId}?automationSurvey=${id}`;
      markOperatorNotificationsReadByDeepLink(deepLink).catch(() => {});
    }
  }, [activePrompt]);



  return (

    <AutomationSurveyModal

      prompt={activePrompt}

      open={modalOpen && Boolean(activePrompt)}

      onClose={handleClose}

      onCompleted={handleCompleted}

    />

  );

}


